<template>
  <section class="mx-auto max-w-[1320px] px-5 py-7 sm:px-8 lg:px-10 lg:py-10">
    <header class="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
      <div>
        <p class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#0066ff]">{{ t('Кабинет врача') }}</p>
        <h1 class="text-3xl font-semibold tracking-tight text-[#182230]">{{ t('Прохождения') }}</h1>
        <p class="mt-2 text-sm text-[#748191]">{{ t('Выберите опросник, укажите отметку и начните прохождение.') }}</p>
      </div>
      <router-link to="/doctor/history" class="inline-flex items-center gap-2 self-start rounded-xl border border-[#dfe5ea] bg-white px-4 py-2.5 text-sm font-medium text-[#3c4856] shadow-sm transition hover:border-[#bfc8d1] sm:self-auto">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
        {{ t('Посмотреть историю') }}
      </router-link>
    </header>

    <div class="mb-7 flex flex-col gap-3 sm:flex-row sm:items-center">
      <label class="relative block flex-1">
        <svg class="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9aa5b1]" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="7" stroke-width="1.8"/><path d="M20 20l-3.5-3.5" stroke-linecap="round" stroke-width="1.8"/></svg>
        <input v-model.trim="search" type="search" class="h-12 w-full rounded-xl border border-[#dfe5ea] bg-white pl-12 pr-4 text-sm outline-none transition placeholder:text-[#9aa5b1] focus:border-[#0066ff] focus:ring-2 focus:ring-[#0066ff]/10" :placeholder="t('Найти опросник')" />
      </label>
      <div class="flex items-center gap-2 rounded-xl border border-[#dfe5ea] bg-white p-1 pl-3">
        <span class="hidden text-xs font-medium text-[#748191] md:inline">{{ t('Язык опросника') }}:</span>
        <div class="flex">
          <button v-for="item in languages" :key="item.value" type="button" class="rounded-lg px-4 py-2 text-sm font-medium transition" :class="language === item.value ? 'bg-[#14213d] text-white shadow-sm' : 'text-[#748191] hover:text-[#182230]'" @click="changeLanguage(item.value)">{{ t(item.label) }}</button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      <div v-for="index in 6" :key="index" class="h-[250px] animate-pulse rounded-2xl border border-[#e5e9ee] bg-white p-6"><div class="h-10 w-10 rounded-xl bg-[#edf0f3]"></div><div class="mt-6 h-5 w-2/3 rounded bg-[#edf0f3]"></div><div class="mt-3 h-4 w-full rounded bg-[#f0f2f4]"></div></div>
    </div>
    <div v-else-if="error" class="rounded-2xl border border-red-100 bg-red-50 p-8 text-center text-sm text-red-700">{{ error }}<button class="ml-2 font-semibold underline" @click="loadSurveys">{{ t('Повторить') }}</button></div>
    <div v-else-if="filteredSurveys.length === 0" class="rounded-2xl border border-dashed border-[#ccd4dc] bg-white px-6 py-16 text-center">
      <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-[#eef2f5] text-[#84909f]"><svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.7" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a3 3 0 006 0M9 5a3 3 0 016 0"/></svg></div>
      <p class="mt-4 font-medium text-[#394554]">{{ t('Опросники не найдены') }}</p>
      <p class="mt-1 text-sm text-[#84909f]">{{ t('Измените запрос или выберите другой язык.') }}</p>
    </div>

    <div v-else class="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
      <article v-for="(survey, index) in filteredSurveys" :key="survey.id" class="group flex min-h-[250px] flex-col rounded-2xl border border-[#dae4f0] bg-[#fcfeff] p-6 shadow-[0_16px_32px_rgba(9,14,29,0.05)] transition hover:-translate-y-0.5 hover:border-[#bfcddd]">
        <div class="flex items-start justify-between">
          <span class="flex h-11 w-11 items-center justify-center rounded-[14px]" :class="cardTone(index)">
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9 12h6m-6 4h4M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a3 3 0 006 0M9 5a3 3 0 016 0"/></svg>
          </span>
          <span class="rounded-full bg-[#f1f4f6] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#748191]">{{ survey.language }}</span>
        </div>
        <h2 class="mt-5 line-clamp-2 text-lg font-semibold leading-6 text-[#202b38]">{{ survey.name }}</h2>
        <p class="mt-2 line-clamp-2 text-sm leading-5 text-[#7b8795]">{{ survey.description || t('Опросник для оценки текущего состояния.') }}</p>
        <div class="mt-auto flex items-center justify-between border-t border-[#edf0f3] pt-5">
          <div class="text-xs text-[#84909f]"><span class="font-semibold text-[#4d5967]">{{ survey.question_count || 0 }}</span> {{ t('вопросов') }}<span v-if="survey.times_completed"> · {{ survey.times_completed }} {{ t('пройдено') }}</span></div>
          <router-link :to="`/doctor/passes/${survey.id}`" class="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0066ff] transition group-hover:gap-2.5">{{ t('Начать') }} <span aria-hidden="true">→</span></router-link>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import api from '@/services/api'
import { t } from '@/i18n'

export default {
  name: 'DoctorSurveys',
  data() {
    return { surveys: [], search: '', language: localStorage.getItem('qamqor_doctor_survey_language') || 'ru', loading: false, error: '', languages: [{ value: 'ru', label: 'Русский' }, { value: 'kz', label: 'Казахский' }] }
  },
  computed: {
    filteredSurveys() {
      const query = this.search.toLocaleLowerCase()
      return this.surveys.filter(item => !query || `${item.name} ${item.description || ''}`.toLocaleLowerCase().includes(query))
    }
  },
  created() { this.loadSurveys() },
  methods: {
    t,
    cardTone(index) { return ['bg-[#e5efff] text-[#0066ff]', 'bg-[#e8f7ec] text-[#27a13d]', 'bg-[#f0f2f5] text-[#667383]'][index % 3] },
    changeLanguage(value) { this.language = value; localStorage.setItem('qamqor_doctor_survey_language', value); this.loadSurveys() },
    async loadSurveys() {
      this.loading = true; this.error = ''
      try { const response = await api.getAvailableSurveys(this.language); this.surveys = response.data || [] }
      catch (error) { this.error = error.response?.data?.error || this.t('Не удалось загрузить опросники') }
      finally { this.loading = false }
    }
  }
}
</script>
